// File builders for the RMN report lists' Download action.
// All PDFs are rendered client-side (@react-pdf/renderer, same as the
// existing single-PDP download); multi-audit reports (category | brand |
// comparison) are packaged as a ZIP: the synthesis "main report" PDF at the
// root plus an audits/ folder with one PDF per successfully audited page.

import React from "react";
import toast from "react-hot-toast";
import JSZip from "jszip";
import { pdf } from "@react-pdf/renderer";
import { PdpAuditPdf } from "./PdpAuditPdf";
import { MultiAuditSynthesisPdf } from "./MultiAuditSynthesisPdf";
import { itemToResponse } from "./MultiAuditSynthesisReport";
import { PdpAudit, PdpAuditResponse } from "./types";
import {
    RmnBrandReportParams,
    RmnCategoryReportParams,
    RmnComparisonReportParams,
    RmnMultiAuditReport,
} from "../../types/rmn";
import { slugify } from "../../utils";

// Minimal structural slice of a pdp_audit_reports row — both PastAudits'
// RmnAuditReportRow and the deep-link page's row satisfy it.
export interface AuditReportRowLike {
    id: number;
    url: string;
    report_type: string;
    params?:
        | RmnCategoryReportParams
        | RmnBrandReportParams
        | RmnComparisonReportParams
        | Record<string, any>
        | null;
    report: PdpAudit | RmnMultiAuditReport<PdpAudit> | null;
}

export type ReportFileType = "application/pdf" | "application/zip";

// Rebuild the PdpAuditResponse shape PdpAuditPdf expects from a stored pdp
// row (the row persists only the audit JSON + url).
export const rowToResponse = (row: AuditReportRowLike): PdpAuditResponse => ({
    audit: row.report as PdpAudit,
    meta: {
        url: row.url,
        finalUrl: row.url,
        title: "",
        metaDescription: "",
        jsonLdBlockCount: 0,
        geminiStatus: "success",
        geminiStatusCode: null,
        geminiError: null,
        reportId: row.id,
    },
});

export function downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

// Filename stem per report type. Derived from params — never row.url (brand
// rows can lack one) and never synthesis.title (absent on partially failed
// rows).
export function reportFileBase(row: AuditReportRowLike): string {
    if (row.report_type === "category") {
        const name = (row.params as RmnCategoryReportParams | null | undefined)
            ?.category_name;
        return `PDP-category-batch-${slugify(name, "category")}`;
    }
    if (row.report_type === "brand") {
        const name = (row.params as RmnBrandReportParams | null | undefined)
            ?.brand_name;
        return `PDP-brand-batch-${slugify(name, "brand")}`;
    }
    if (row.report_type === "comparison") {
        const items = (row.report as RmnMultiAuditReport<PdpAudit> | null)?.items || [];
        return `pdp-comparison-${items.length}-pages`;
    }
    return slugify((row.report as PdpAudit | null)?.productName, "product");
}

// Let the browser paint between sequential PDF renders — react-pdf works on
// the main thread.
export const yieldToUi = () =>
    new Promise<void>((resolve) => {
        if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(() => resolve());
        } else {
            setTimeout(resolve, 0);
        }
    });

export async function buildPdpAuditPdfBlob(row: AuditReportRowLike): Promise<Blob> {
    return pdf(<PdpAuditPdf data={rowToResponse(row)} />).toBlob();
}

export async function buildSynthesisPdfBlob(row: AuditReportRowLike): Promise<Blob> {
    return pdf(
        <MultiAuditSynthesisPdf
            reportType={row.report_type}
            report={row.report as RmnMultiAuditReport<PdpAudit>}
            params={
                row.params as
                    | RmnCategoryReportParams
                    | RmnBrandReportParams
                    | RmnComparisonReportParams
                    | null
            }
        />,
    ).toBlob();
}

export async function buildMultiReportZip(row: AuditReportRowLike): Promise<Blob> {
    const base = reportFileBase(row);
    const zip = new JSZip();
    zip.file(`${base}.pdf`, await buildSynthesisPdfBlob(row));
    const folder = zip.folder("audits");
    const items = (row.report as RmnMultiAuditReport<PdpAudit> | null)?.items || [];
    for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        // Failed items have no audit to render; they're listed in the
        // synthesis PDF's Individual Page Audits index instead.
        if (item.audit) {
            // Sequential on purpose — parallel renders contend for the main
            // thread and hold every intermediate PDF in memory at once.
            // eslint-disable-next-line no-await-in-loop
            const blob = await pdf(<PdpAuditPdf data={itemToResponse(item)} />).toBlob();
            // Index prefix keeps ranking order and prevents JSZip silently
            // overwriting duplicate product names.
            const name = `${String(i + 1).padStart(2, "0")}-${slugify(
                item.audit.productName || item.url,
                "audit",
            )}.pdf`;
            folder?.file(name, blob);
            // eslint-disable-next-line no-await-in-loop
            await yieldToUi();
        }
    }
    return zip.generateAsync({ type: "blob", compression: "DEFLATE" });
}

export interface ReportDownload {
    blob: Blob;
    filename: string;
    fileType: ReportFileType;
}

// pdp rows produce the single audit PDF, multi rows the full ZIP.
export async function buildReportDownload(row: AuditReportRowLike): Promise<ReportDownload> {
    const base = reportFileBase(row);
    if (row.report_type === "pdp") {
        return {
            blob: await buildPdpAuditPdfBlob(row),
            filename: `PDP-Audit-${base}.pdf`,
            fileType: "application/pdf",
        };
    }
    return {
        blob: await buildMultiReportZip(row),
        filename: `${base}.zip`,
        fileType: "application/zip",
    };
}

// ---------------------------------------------------------------------------
// One-call downloads for the Catalog pages' per-row "download latest report"
// buttons (fetch + build + toast in one place, so the three tables stay in
// lockstep with the history sections' Download action).
// ---------------------------------------------------------------------------

// Category/brand reports: fetch the full pdp_audit_reports row and package
// it (synthesis PDF + audits ZIP).
// `rmnBrandId`: internal users outside a brand workspace must name the root
// they act as (rmnContext) — the internal Brands page passes the brand itself.
export async function downloadRmnReportById(reportId: number, rmnBrandId?: number | null): Promise<void> {
    // Lazy requires keep this module importable in the headless PDF smoke
    // harness (no axios/toast there).
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const axiosInstant = require("../../api").default;
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { rmnAuditReportEndpoint } = require("../../api/endpoints");
    const toastId = toast.loading("Building the report package… this can take a minute.");
    try {
        const res = await axiosInstant.get(
            rmnBrandId ? `${rmnAuditReportEndpoint(reportId)}?rmn_brand_id=${rmnBrandId}` : rmnAuditReportEndpoint(reportId),
        );
        const { blob, filename } = await buildReportDownload(res.data as AuditReportRowLike);
        downloadBlob(blob, filename);
        toast.success("Report downloaded", { id: toastId });
    } catch {
        toast.error("Couldn't build the report file. Please try again.", { id: toastId });
    }
}

// Product rows: fetch one stored audit run (pdp_url_scores) and render the
// single-PDP PDF.
export async function downloadPdpScorePdf({
    productId,
    pdpUrlId,
    scoreId,
    url,
}: {
    productId: number;
    pdpUrlId: number;
    scoreId: number;
    url: string;
}): Promise<void> {
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const axiosInstant = require("../../api").default;
    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const { getProductPdpUrlScore } = require("../../api/endpoints");
    const toastId = toast.loading("Preparing PDF…");
    try {
        const res = await axiosInstant.get(getProductPdpUrlScore(productId, pdpUrlId, scoreId));
        const row: AuditReportRowLike = {
            id: scoreId,
            url,
            report_type: "pdp",
            params: null,
            report: (res.data && res.data.report) || null,
        };
        if (!row.report) throw new Error("NoReport");
        const { blob, filename } = await buildReportDownload(row);
        downloadBlob(blob, filename);
        toast.success("Report downloaded", { id: toastId });
    } catch {
        toast.error("Couldn't build the report file. Please try again.", { id: toastId });
    }
}
